// src/pages/Contacts/components/ContactFormModal.tsx
import React, { useState, useEffect } from "react";
import { X, Save } from "lucide-react";
import { useCreateContactMutation, useUpdateContactMutation } from "../../../store/api/contactApi";
import { useToast } from "../../../hooks/common/useToast";
import type { ContactResponseDto, CreateContactDto, UpdateContactDto, ContactStatus } from "../../../types/contact";

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  contact?: ContactResponseDto | null;
  mode: "create" | "edit";
}

const ContactFormModal: React.FC<ContactFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  contact,
  mode,
}) => {
  const [formData, setFormData] = useState<CreateContactDto & { status?: ContactStatus }>({
    name: "",
    contact: "",
    status: "active",
  });

  const [createContact, { isLoading: isCreating }] = useCreateContactMutation();
  const [updateContact, { isLoading: isUpdating }] = useUpdateContactMutation();
  const { showToast } = useToast();

  const isLoading = isCreating || isUpdating;

  // Populate form when editing
  useEffect(() => {
    if (mode === "edit" && contact) {
      setFormData({
        name: contact.name,
        contact: contact.contact,
        status: contact.status,
      });
    } else if (mode === "create") {
      setFormData({
        name: "",
        contact: "",
        status: "active",
      });
    }
  }, [mode, contact, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.contact.trim()) {
      showToast("error", "Please fill in all required fields");
      return;
    }

    try {
      if (mode === "create") {
        await createContact(formData).unwrap();
      } else if (mode === "edit" && contact) {
        // Only include editable fields (contact and status, not name)
        const updateData: UpdateContactDto = {
          contact: formData.contact,
          status: formData.status,
        };
        await updateContact({ id: contact.id, ...updateData }).unwrap();
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.data?.message || `Failed to ${mode} contact`;
      showToast("error", errorMessage);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {mode === "create" ? "Add New Contact" : "Edit Contact"}
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={onClose}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Facebook, Viber, Email"
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    mode === "edit" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                  required
                  disabled={mode === "edit"}
                  readOnly={mode === "edit"}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {mode === "edit" 
                    ? "Contact name cannot be modified in edit mode" 
                    : "Contact platform or method name (Facebook, Viber, Email, etc.)"}
                </p>
              </div>

              {/* Contact Information */}
              <div>
                <label htmlFor="contact" className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Information <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="contact"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  placeholder="e.g., https://facebook.com/zawhtet, +95912345678, contact@zawhtet.com"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the actual contact details (URL, phone number, email address, etc.)
                </p>
              </div>

              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {mode === "create" ? "Creating..." : "Updating..."}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {mode === "create" ? "Create Contact" : "Update Contact"}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactFormModal;